import { findAllIterator, OptionsWithRql, rqlBuilder, UserConfiguration, UserData } from '@extrahorizon/javascript-sdk';
import dayjs from 'dayjs';
import { sdk, SCHEMA_NAMES, RAW_ENDPOINTS, queryParams } from 'utils/query';
import { createAllPatientProfile, getProfile } from 'services/profile';
import {
  ETLPatientProfile,
  Indicator,
  PatientHasMeasurement,
  PatientProfile,
  PatientStatus,
  PORTAL_PATIENT_ID_PREFIX,
  ReportsStatus,
  ReportsTableItem,
  Staff,
  UserProfile,
} from 'utils/models';
import { getGroups } from 'services/groups';
import { getDocumentToApprove, getGroupConfiguration, getUserConfigurationById } from 'services/configuration';
import { getGeneralConfiguration } from 'hooks/api/configuration/useGetGeneralConfiguration';
import { FilterReportsFieldNames } from 'models/filter';
import { cleanUtils, getActiveGroupIds } from 'utils/filters';
import { TableInput } from 'models/general';
import { DataFieldNames } from 'pages/Main/Patients/PatientsTable.config';
import { CurrentUserData, Enlistment, PatientData } from 'models/user';
import { sortEnlistments } from 'utils/sort';
import { setUser } from '@sentry/react';
import { GroupEnlistment } from 'models/groups';
import { PatientSearchVariables } from 'models/profile';
import { HeartRhythmMeasurementIndicatorMap } from 'models/heartRhythm/measurement';
import DataLoader from 'dataloader';
import { EmptyParametersError } from './errors/EmptyParametersError';
import { ObjectNotFoundError } from './errors/ObjectNotFoundError';
import { ElementNotFoundError } from './errors/ElementNotFoundError';
import { NoStaffError } from './errors/NoStaffError';

export const logoutCurrentUser = async () => {
  await sdk.raw.post(`logout`);
};

export const getMe = () => {
  return sdk.users.me();
};

export const getUserData = async (): Promise<CurrentUserData> => {
  const user = await getMe();

  const { staffEnlistments } = user;

  if (!staffEnlistments || !(staffEnlistments?.length > 0)) {
    throw new NoStaffError();
  }

  setUser({ id: user.id });

  const userConfiguration: UserConfiguration = await getUserConfigurationById(user.id || '');
  const generalConfiguration = await getGeneralConfiguration();
  const documentsToApprove = getDocumentToApprove(userConfiguration, generalConfiguration);
  const groups = await getGroups(staffEnlistments, userConfiguration);
  const profile = await getProfile(user.id || '');
  const mfaSetting = await getUserMFASetting(user.id || '');
  const visibleGroups = groups.filter((group) => !group.hidden);
  const visibleGroupsIds = visibleGroups.map((group) => group.id);

  const { fdaAllowedDeviceModels } = generalConfiguration.data;

  return {
    user,
    profile,
    mfaSetting,
    groups,
    documentsToApprove,
    visibleGroups,
    visibleGroupsIds,
    userConfiguration,
    fdaAllowedDeviceModels,
  };
};

export const getPatientData = async (variables: {
  patientId?: string;
  visibleGroupsIds?: string[];
  activeGroups?: GroupEnlistment[];
}): Promise<PatientData> => {
  const { patientId, visibleGroupsIds, activeGroups } = variables;
  if (!patientId || !visibleGroupsIds || !activeGroups) throw new EmptyParametersError();

  const rql = rqlBuilder().eq('id', patientId).build();
  const result = await sdk.users.patients({
    rql,
  });

  if (result.data.length !== 1) throw new ElementNotFoundError();

  const patient = result.data[0];
  const patientEnlistments = patient.patientEnlistments?.filter((group) => visibleGroupsIds.includes(group.groupId));

  if (!patientEnlistments) throw new ObjectNotFoundError(`PatientEnlistments (id:${patientId})`);

  const bloodPressureEnabledGroupsIds: string[] = [];
  const weightEnabledGroupsIds: string[] = [];
  const visibleEnlistedGroupsIds: string[] = [];
  const visibleEnlistments: Enlistment[] = [];

  await Promise.all(
    patientEnlistments.map(async (enlistment) => {
      visibleEnlistedGroupsIds.push(enlistment.groupId);
      const groupName = activeGroups.find((group) => group.id === enlistment.groupId)?.name;
      visibleEnlistments.push({ ...enlistment, name: groupName || '??' });

      const groupConfig = await getGroupConfiguration(enlistment.groupId);

      if (groupConfig?.patientConfiguration?.bloodPressure?.enabled) {
        bloodPressureEnabledGroupsIds.push(enlistment.groupId);
      }
      if (groupConfig?.patientConfiguration?.weightBmi?.enabled) {
        weightEnabledGroupsIds.push(enlistment.groupId);
      }
    }),
  );

  return {
    ...patient,
    patientEnlistments,
    bloodPressureEnabledGroupsIds,
    weightEnabledGroupsIds,
    visibleEnlistedGroupsIds,
    visibleEnlistments: sortEnlistments(visibleEnlistments),
  };
};

export function getUsers(ids: string[], limit = 20) {
  return sdk.users.find({
    rql: rqlBuilder().in('id', ids).limit(limit).build(),
  });
}

export const getPatientByName = async ({ searchValue }: PatientSearchVariables) => {
  const firstNameFilter = rqlBuilder().like(FilterReportsFieldNames.FirstName, searchValue).intermediate();
  const lastNameFilter = rqlBuilder().like(FilterReportsFieldNames.LastName, searchValue).intermediate();

  const filterList = [firstNameFilter, lastNameFilter];

  const rql = rqlBuilder()
    .or(...filterList.filter((filter) => filter))
    .build();

  const response = await sdk.users.patients({
    rql,
  });

  return response.data;
};

export function getUserMFASetting(id: string) {
  return sdk.auth.users.getMfaSetting(id);
}

export async function updatePersonalData(values: UserProfile) {
  const rql = rqlBuilder().eq('id', values.id).build();

  await sdk.users.update(values.id, {
    firstName: values.firstName,
    lastName: values.lastName,
    phoneNumber: values.phoneNumber,
    language: values.language,
  });

  await sdk.users.updateEmail(values.id, { email: values.email || '' });

  await sdk.profiles.update(rql, {
    country: values.country,
    birthday: values.birthday,
    gender: values.gender,
  });

  if (values.physicianIdentifier === '') {
    await sdk.configurations.users.removeFields(values.id, { fields: ['data.physicianIdentifier'] });
  }

  await sdk.configurations.users.update(values.id, {
    data: {
      measurementSystem: values.measurementSystem,
      hourFormat: values.hourFormat,
      physicianIdentifier: values.physicianIdentifier === '' ? undefined : values.physicianIdentifier,
    },
  });
}

export async function getTotalPatients(visibleGroupsIds?: string[]) {
  if (!visibleGroupsIds) return 0;

  const rql = rqlBuilder().in('patient_enlistments.group_id', visibleGroupsIds).build();
  const { page } = await sdk.users.patients({ rql });

  return page.total;
}

export async function getTotalActivePatients(visibleGroupsIds?: string[]) {
  if (!visibleGroupsIds) return 0;

  const rql = rqlBuilder()
    .contains(
      'patient_enlistments',
      rqlBuilder().in('group_id', visibleGroupsIds).intermediate(),
      rqlBuilder().eq('expired', 'false').intermediate(),
    )
    .build();
  const { page } = await sdk.users.patients({ rql });

  return page.total;
}

export async function getPatients({ pageParam, filters, currentUser, config, limit = 20 }: TableInput) {
  if (!currentUser) throw new EmptyParametersError();
  const relevantGroupIds = getActiveGroupIds(currentUser.visibleGroupsIds, filters.groups);
  const filterList = [rqlBuilder().in(DataFieldNames.groupId, relevantGroupIds).intermediate()];

  if (filters.firstname) {
    filterList.push(
      rqlBuilder().like(DataFieldNames.cleanedFirstName, cleanUtils.clean(filters.firstname)).intermediate(),
    );
  }

  if (filters.lastname) {
    filterList.push(
      rqlBuilder().like(DataFieldNames.cleanedLastName, cleanUtils.clean(filters.lastname)).intermediate(),
    );
  }

  if (filters[queryParams.patientId]) {
    filterList.push(
      rqlBuilder()
        .like(DataFieldNames.externalPatientIds, `${PORTAL_PATIENT_ID_PREFIX}${filters[queryParams.patientId].trim()}`)
        .intermediate(),
    );
  }

  if (filters.status && filters.status !== PatientStatus.All) {
    const now = Date.now();
    if (filters.status === PatientStatus.Active) {
      filterList.push(rqlBuilder().ge(DataFieldNames.prescriptionEndTimestamp, now.toString()).intermediate());
    } else {
      filterList.push(rqlBuilder().lt(DataFieldNames.prescriptionEndTimestamp, now.toString()).intermediate());
    }
  }

  if (filters.hasMeasurement && filters.hasMeasurement !== PatientHasMeasurement.All) {
    if (filters.hasMeasurement === PatientHasMeasurement.Yes) {
      filterList.push(rqlBuilder().contains(DataFieldNames.lastMeasurement).intermediate());
    } else {
      filterList.push(rqlBuilder().excludes(DataFieldNames.lastMeasurement).intermediate());
    }
  }

  if (filters.lastMeasurementResultType) {
    const selectedMeasurementResultColors = filters.lastMeasurementResultType
      .split(',')
      .map((indicator) => HeartRhythmMeasurementIndicatorMap[indicator as Indicator].filterName);
    filterList.push(
      rqlBuilder().in(DataFieldNames.lastMeasurementResultColor, selectedMeasurementResultColors).intermediate(),
    );
  }

  if (filters.lastMostSevereMeasurementResultType) {
    const selectedMeasurementResultColors = filters.lastMostSevereMeasurementResultType
      .split(',')
      .map((indicator) => HeartRhythmMeasurementIndicatorMap[indicator as Indicator].filterName);
    filterList.push(
      rqlBuilder()
        .in(DataFieldNames.lastMostSevereMeasurementResultColor, selectedMeasurementResultColors)
        .intermediate(),
    );
  }

  if (filters[queryParams.startDate]) {
    filterList.push(rqlBuilder().gt(DataFieldNames.lastMeasurementDate, filters[queryParams.startDate]).intermediate());
  }

  if (filters[queryParams.endDate]) {
    filterList.push(rqlBuilder().lt(DataFieldNames.lastMeasurementDate, filters[queryParams.endDate]).intermediate());
  }

  if (filters[queryParams.birthday]) {
    const birthdayTimestamp = +filters[queryParams.birthday];
    filterList.push(
      rqlBuilder()
        .ge(DataFieldNames.birthday, dayjs(birthdayTimestamp).startOf('day').valueOf().toString())
        .intermediate(),
    );
    filterList.push(
      rqlBuilder()
        .le(DataFieldNames.birthday, dayjs(birthdayTimestamp).endOf('day').valueOf().toString())
        .intermediate(),
    );
  }

  const sort = filters.sort || config.defaultSort || '';

  const filteringRql = rqlBuilder()
    .select(DataFieldNames.userId)
    .and(...filterList.filter((filter) => filter))
    .sort(sort)
    .limit(limit, pageParam * limit)
    .build();

  const filteredResult = await sdk.data.documents.find<ETLPatientProfile>(SCHEMA_NAMES.physicianDashboardTableRows, {
    rql: filteringRql,
  });

  const patientIds = filteredResult.data.map((result) => result.data?.user.id) as string[];

  const patientDataRql = rqlBuilder()
    .in(DataFieldNames.userId, patientIds)
    .in(DataFieldNames.groupId, relevantGroupIds)
    .sort(sort)
    .build();

  const result = await sdk.data.documents.findAll<ETLPatientProfile>(SCHEMA_NAMES.physicianDashboardTableRows, {
    rql: patientDataRql,
  });

  const patientsWithExtraData = result.map((patient) => patient.data) as ETLPatientProfile[];

  const patients: PatientProfile[] = await createAllPatientProfile(patientsWithExtraData);

  const filteredPatients: PatientProfile[] = patients.filter((patient: PatientProfile) => {
    const filterLastMeasurementIndicators = filters.lastMeasurementResultType
      ? filters?.lastMeasurementResultType.split(',')
      : '';

    const patientLastMeasurementIndicator = patient.lastMeasurement?.indicator as string;

    const lastMeasurementCondition =
      filterLastMeasurementIndicators.includes('') ||
      filterLastMeasurementIndicators.includes(patientLastMeasurementIndicator);

    const filterLastMostSevereMeasurementIndicators = filters.lastMostSevereMeasurementResultType
      ? filters.lastMostSevereMeasurementResultType.split(',')
      : '';

    const patientLastMostSevereMeasurementIndicator = patient.mostSevereMeasurement?.indicator as string;

    const lastMostSevereMeasurementCondition =
      filterLastMostSevereMeasurementIndicators.includes('') ||
      filterLastMostSevereMeasurementIndicators.includes(patientLastMostSevereMeasurementIndicator);

    return lastMeasurementCondition && lastMostSevereMeasurementCondition;
  });

  // ON HOLD: https://qompium.atlassian.net/browse/FIBWEB-1681
  // Dirty way to clean out last measurements still appearing on the Patients List even when
  // "no measurements" are selected, this happens when multiple groups are selected causing the
  // edge case to occur
  let doubleCheckLastMeasurement: PatientProfile[] = [];
  if (filters?.hasMeasurement === PatientHasMeasurement.No) {
    doubleCheckLastMeasurement = filteredPatients.filter((patient) => !patient.lastMeasurement);
  }

  return {
    ...filteredResult,
    data: doubleCheckLastMeasurement.length === 0 ? filteredPatients : doubleCheckLastMeasurement,
  };
}

export async function getStaff({ pageParam, params, limit = 20 }: TableInput) {
  if (!params.groupId) throw new EmptyParametersError();

  const filteringRql = rqlBuilder()
    .in('staff_enlistments.roles.group_id', [params.groupId])
    .limit(limit, pageParam * limit)
    .build();

  const result = await sdk.users.staff({ rql: filteringRql });
  return {
    ...result,
    data: result.data.flatMap((item): Staff[] =>
      item.id && !item.email?.toLowerCase().endsWith('@fibricheck.com') // hide FibriCheck employee accounts from the list of staff members
        ? [
            {
              id: item.id,
              firstName: item.firstName ?? '',
              lastName: item.lastName ?? '',
              email: item.email ?? '',
              phoneNumber: item.phoneNumber ?? '',
            },
          ]
        : [],
    ),
  };
}

export async function getReports({ pageParam, filters, currentUser, limit = 20 }: TableInput) {
  if (!currentUser) throw new EmptyParametersError();
  const relevantGroupIds = getActiveGroupIds(currentUser.visibleGroupsIds, filters[queryParams.groups]);
  const filterList = [
    rqlBuilder().in(FilterReportsFieldNames.GroupId, relevantGroupIds).intermediate(),
    rqlBuilder()
      .or(
        rqlBuilder().ne(FilterReportsFieldNames.Status, 'NEW').intermediate(),
        rqlBuilder().eq(FilterReportsFieldNames.Trigger, 'MANUAL').intermediate(),
      )
      .intermediate(),
  ];

  if (filters.id) {
    filterList.push(rqlBuilder().eq(FilterReportsFieldNames.UserId, filters.id).intermediate());
  }

  if (filters.status && filters.status !== ReportsStatus.All) {
    filterList.push(rqlBuilder().eq(FilterReportsFieldNames.Status, filters.status).intermediate());
  }

  const filteringRql = rqlBuilder()
    .and(...filterList.filter((filter) => filter))
    .limit(limit, pageParam * limit)
    .sort(`-${FilterReportsFieldNames.CreationTimestamp}`)
    .build();

  const configuration = await sdk.raw.get(`${RAW_ENDPOINTS.REPORTS}/${filteringRql}`);

  // Get first and last name for the userId present in the Reports Table
  const userIds = configuration.data?.data?.map((item: ReportsTableItem) => item.userId) || [];

  if (userIds) {
    const userNamesList = (await getUserNames(userIds || [])) as UserData[];

    if (userNamesList) {
      for (const oldUser of configuration.data.data) {
        const tempUser = userNamesList.find((user: UserData) => oldUser.userId === user.id);

        oldUser.firstName = tempUser?.firstName;
        oldUser.lastName = tempUser?.lastName;
      }
    }
  }
  return configuration.data;
}

export async function getUserNames(ids: string[]) {
  if (ids.length === 0) return {};
  const rql = rqlBuilder().in('id', ids).select(['id', 'first_name', 'last_name']).build();

  const find = async (options: OptionsWithRql) => await sdk.users.patients(options);
  const iterator = findAllIterator(find, { rql });

  const users: Partial<UserData>[] = [];

  for await (const user of iterator) {
    users.push(...user.data);
  }

  return users;
}

export const sendActivationEmail = async (variables: { email: string }) => {
  return await sdk.users.requestEmailActivation(variables.email);
};

export const userLoader = new DataLoader(
  async (ids: readonly string[]) => {
    const users = await getUsers([...ids], 20);
    const userMap = new Map(users.data.map((u) => [u.id, u]));
    return ids.map((id) => userMap.get(id) || null);
  },
  {
    // Cache using React Query
    cache: false,
    maxBatchSize: 20,
  },
);
